/**
 * ConfirmStep.tsx
 * Step 2: Review network stats, explain the two-phase sync, confirm.
 * Calls POST /api/ingestion/start on confirm.
 */

import { useState } from "react";
import { startIngestion } from "../../lib/api";
import type { NetworkSummary } from "../../lib/types";
import Spinner from "../../components/Spinner";

interface Props {
  network: NetworkSummary;
  onSyncStarted: (jobId: string) => void;
  onBack: () => void;
}

// TODO: replace with real customer ID from Supabase Auth session
const TEMP_CUSTOMER_ID = "dev-customer-placeholder";

export default function ConfirmStep({ network, onSyncStarted, onBack }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleConfirm() {
    setError("");
    setLoading(true);
    try {
      const result = await startIngestion(TEMP_CUSTOMER_ID, network.id);
      onSyncStarted(result.job_id);
    } catch (err: any) {
      setError(err.message ?? "Failed to start sync. Please try again.");
      setLoading(false);
    }
  }

  const stats = [
    { label: "Members",  value: network.member_count,  icon: "👤" },
    { label: "Posts",    value: network.post_count,     icon: "📝" },
    { label: "Events",   value: network.event_count,    icon: "📅" },
    { label: "Spaces",   value: network.space_count,    icon: "🗂️" },
  ].filter(s => s.value != null);

  return (
    <div className="step-card">
      <div className="logo">
        <div className="logo-icon">⚡</div>
        <span className="logo-text">Hostology</span>
      </div>

      <div className="step-title-row">
        <span className="step-check">✅</span>
        <h1 className="step-title">Community found</h1>
      </div>
      <p className="step-sub">We verified your credentials. Here's what we'll mirror.</p>

      <div className="network-badge">
        <div className="network-badge-icon">🏛️</div>
        <div>
          <div className="network-badge-name">{network.title}</div>
          <div className="network-badge-sub">{network.subdomain}</div>
        </div>
      </div>

      {stats.length > 0 && (
        <div className="stat-grid">
          {stats.map(s => (
            <div key={s.label} className="stat-card">
              <span className="stat-icon">{s.icon}</span>
              <span className="stat-value">{s.value!.toLocaleString()}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="info-box">
        <div className="info-box-title">⚡ What happens next</div>
        <ul className="info-list">
          <li>Your most recent ~500 posts sync immediately <span className="tag">Phase 1</span></li>
          <li>Widgets and export are available once Phase 1 completes</li>
          <li>Full history backfills quietly in the background <span className="tag">Phase 2</span></li>
        </ul>
      </div>

      {error && (
        <div className="error-banner" role="alert">
          ⚠️ {error}
        </div>
      )}

      <button className="btn-primary" onClick={handleConfirm} disabled={loading}>
        {loading ? (
          <>
            <Spinner size={16} /> Starting sync…
          </>
        ) : (
          "Start sync →"
        )}
      </button>

      <button className="btn-secondary" onClick={onBack} disabled={loading}>
        ← Use different credentials
      </button>
    </div>
  );
}
